/**
 * usePipelineStats — Phase G · Frontend Pipeline Health Banner
 *
 * Polls `GET /api/admin/pipeline-stats` every 30 s and returns the latest
 * snapshot of the three media-processing pipelines (Render / Harvest /
 * Frames) plus a rolled-up 24-hour job-history breakdown.
 *
 * Endpoint shape (from routes/admin.py · pipeline-stats):
 *   {
 *     pipelines: {
 *       render:   { executor: "modal" | "local", label, kind, flag },
 *       harvest:  { executor: "modal" | "local", label, kind, flag },
 *       frames:   { executor: "modal" | "local", label, kind, flag },
 *     },
 *     recent_jobs: {
 *       since: ISO timestamp,
 *       by_kind: { [kind]: { done, failed, running, … } },
 *       by_executor: { modal, local, unknown },
 *     },
 *     modal: { app_name, has_credentials },
 *   }
 *
 * The hook stays silent on transient network blips — the banner just
 * renders the LAST successful snapshot so the admin never sees flicker
 * when polling glitches for a single round-trip.
 *
 * Anomaly derivation (Phase G · Active Alerting):
 *   hasAnomaly      — true when ANY anomaly signal fires
 *   anomalyReasons  — array of human-readable reason strings
 *     • credentials_missing : Modal tokens absent → all jobs forced local
 *     • failures_exceed     : >5 failed jobs across all kinds in 24h
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { deriveAnomaly } from "@/lib/pipelineAnomaly";

const POLL_INTERVAL_MS = 30_000;

export default function usePipelineStats() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;

    const tick = async () => {
      try {
        const r = await api.get("/admin/pipeline-stats", { timeout: 8_000 });
        if (!mountedRef.current) return;
        setData(r.data);
        setError(null);
      } catch (e) {
        // Keep the previous `data` so the banner doesn't blank between
        // failed polls. Surface the error separately for the tooltip.
        if (!mountedRef.current) return;
        setError(e?.message || "fetch failed");
      } finally {
        if (mountedRef.current) setLoading(false);
      }
    };

    tick(); // fire immediately on mount
    const id = setInterval(tick, POLL_INTERVAL_MS);
    return () => {
      mountedRef.current = false;
      clearInterval(id);
    };
  }, []);

  const anomaly = useMemo(() => deriveAnomaly(data), [data]);

  return { data, error, loading, ...anomaly };
}
